/* global Office */

/**
 * Reads the message currently open in read mode and returns what AroundLink needs to file it:
 * sender, recipients, subject, date and plain-text body.
 */

function address(details) {
  return details ? { email: details.emailAddress || "", name: details.displayName || null } : null;
}

/** @returns {Promise<string>} */
function readBody(item) {
  return new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
      } else {
        reject(
          new Error(result.error ? result.error.message : "The message body could not be read.")
        );
      }
    });
  });
}

/**
 * The HTML body. Best-effort: if it cannot be read we resolve to "" rather than fail, since the
 * plain-text body already carries the content and must not be lost over the formatting.
 *
 * @returns {Promise<string>}
 */
function readHtml(item) {
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Html, (result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "");
    });
  });
}

/**
 * @returns {Promise<{
 *   messageId: string|null, itemId: string|null,
 *   from: {email: string, name: string|null}|null,
 *   to: Array<{email: string, name: string|null}>, cc: Array<{email: string, name: string|null}>,
 *   subject: string, date: string|null, body: string, bodyHtml: string, mailbox: string
 * }>}
 */
export async function readCurrentMessage() {
  const item = Office.context.mailbox.item;
  if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) {
    throw new Error("No message is open.");
  }

  const date = item.dateTimeCreated instanceof Date ? item.dateTimeCreated.toISOString() : null;

  return {
    // The RFC Message-ID: the same on every copy of the message, which is what lets the
    // server recognise one it already has.
    messageId: item.internetMessageId || null,
    itemId: item.itemId || null,
    from: address(item.from),
    to: (item.to || []).map(address),
    cc: (item.cc || []).map(address),
    subject: item.subject || "",
    date,
    body: await readBody(item),
    bodyHtml: await readHtml(item),
    // Whose mailbox this is — the AroundLink user the message belongs to.
    mailbox: Office.context.mailbox.userProfile.emailAddress,
  };
}

/** Reads one compose-mode field, which is only available through getAsync. */
function getAsync(run) {
  return new Promise((resolve, reject) => {
    run((result) =>
      result.status === Office.AsyncResultStatus.Succeeded
        ? resolve(result.value)
        : reject(new Error(result.error ? result.error.message : "A field could not be read."))
    );
  });
}

/**
 * Reads the message being composed — used on send (OnMessageSend), when the user clicks Send.
 * The sender is the mailbox owner (one of ours), so the server files it as outgoing. There is no
 * Message-ID yet (the server assigns it on send), so the server dedupes on from + date + subject.
 *
 * @returns {Promise<{
 *   messageId: null, itemId: null,
 *   from: {email: string, name: string|null},
 *   to: Array<{email: string, name: string|null}>, cc: Array<{email: string, name: string|null}>,
 *   subject: string, date: string, body: string, bodyHtml: string, mailbox: string
 * }>}
 */
export async function readComposeMessage() {
  const item = Office.context.mailbox.item;
  if (!item) {
    throw new Error("No message is being composed.");
  }

  const [subject, body, html, to, cc] = await Promise.all([
    getAsync((cb) => item.subject.getAsync(cb)),
    getAsync((cb) => item.body.getAsync(Office.CoercionType.Text, cb)),
    readHtml(item),
    getAsync((cb) => item.to.getAsync(cb)),
    getAsync((cb) => item.cc.getAsync(cb)),
  ]);

  const profile = Office.context.mailbox.userProfile;

  return {
    messageId: null,
    itemId: null,
    from: { email: profile.emailAddress || "", name: profile.displayName || null },
    to: (to || []).map(address),
    cc: (cc || []).map(address),
    subject: subject || "",
    date: new Date().toISOString(),
    body: body || "",
    bodyHtml: html || "",
    mailbox: profile.emailAddress || "",
  };
}
